import { type ColumnDef, type RowSelectionState } from "@tanstack/react-table";

import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { TABLE_COLUMN_WIDTHS } from "../constants";

export const createSelectionColumn = <TData,>(
  getCanSelect?: (row: TData) => boolean,
  onRowSelectionChange?: (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState),
  ) => void,
  compactCheckbox?: boolean,
  hideUnselectableCheckboxes?: boolean,
): ColumnDef<TData, unknown> => ({
  id: "select",
  header: ({ table }) => {
    // Get all rows and filter out the ones that can be selected
    const allRows = table.getRowModel().rows;
    const selectableRows = getCanSelect
      ? allRows.filter((row) => getCanSelect(row.original))
      : allRows;

    if (hideUnselectableCheckboxes && selectableRows.length === 0) {
      return null;
    }

    const onChange = (value: boolean) => {
      if (getCanSelect) {
        // Manually select/deselect only the rows that can be selected
        selectableRows.forEach((row) => {
          row.toggleSelected(!!value);
        });
      } else {
        // If no getCanSelect function, use the default behavior
        table.toggleAllRowsSelected(!!value);
      }
    };

    const allSelectableSelected = getCanSelect
      ? selectableRows.every((row) => row.getIsSelected())
      : table.getIsAllRowsSelected();

    return (
      <Checkbox
        aria-label="Select all rows"
        checked={allSelectableSelected}
        className={cnTwMerge("flex items-center justify-center", {
          "!pr-0": compactCheckbox,
        })}
        data-testid="select-all-checkbox"
        onCheckedChange={onChange}
      />
    );
  },
  cell: ({ row }) => {
    const isSelected = row.getIsSelected();
    const rowId = row.id;
    const rowIndex = row.index;
    const canSelect = getCanSelect ? getCanSelect(row.original) : true;

    // If hideUnselectableCheckboxes is true and row cannot be selected, render nothing
    if (hideUnselectableCheckboxes && !canSelect) {
      return null;
    }

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          aria-label={`Select row ${rowIndex + 1}`}
          checked={isSelected}
          className={cnTwMerge("flex items-center justify-center", {
            "!pr-0": compactCheckbox,
          })}
          data-testid={`select-row-${rowId}`}
          disabled={!canSelect}
          onCheckedChange={(value: boolean) => {
            if (canSelect) {
              if (typeof onRowSelectionChange === "function") {
                onRowSelectionChange((prev: RowSelectionState) => {
                  if (value) {
                    return { ...prev, [rowId]: true };
                  } else {
                    // eslint-disable-next-line unused-imports/no-unused-vars
                    const { [rowId]: _, ...rest } = prev;

                    return rest;
                  }
                });
              } else {
                row.toggleSelected(!!value);
              }
            }
          }}
        />
      </div>
    );
  },
  enableSorting: false,
  enableHiding: false,
  isMobileSelector: true,
  isMobileHeader: true,
  size: compactCheckbox
    ? TABLE_COLUMN_WIDTHS.SELECTION_COMPACT
    : TABLE_COLUMN_WIDTHS.SELECTION,
  align: "center",
  cellHeaderClassName: "!pr-0 !pl-5",
  className: "!pr-0 !pl-5",
});
